/**
 * normalizeStatusConfig.js
 *
 * Presentation metadata for `VoiceSnippet.normalizeStatus` — the pre-clone
 * canonicalization step owned by `server/services/voiceCloning/SampleNormalizer.js`.
 * Each recorded sample passes through this stage before the clone gate in
 * `VoiceCloningService.seedCloneForSample` will ship it to ElevenLabs.
 *
 * Server authority (see SampleNormalizer.NORMALIZE_STATUS):
 *
 *   pending      Enqueued, waiting for the worker to pick it up
 *   processing   Worker is downloading + ffprobe/ffmpeg transcoding
 *   ready        Canonical WAV in S3; `VoiceSnippet.s3Url` has been flipped
 *                over to it; clone pipeline is free to proceed
 *   failed       Transient/infra failure (e.g. ffprobe missing, S3 timeout).
 *                User can hit "Retry" to re-enqueue.
 *   rejected     Permanent refusal — no audio stream, duration exceeds the
 *                VOICE_SAMPLE_MAX_SECONDS ceiling, etc. Retry is NOT helpful;
 *                the actor needs to re-record.
 *
 * Plus two virtual client-only rows:
 *
 *   none         VoiceSnippet exists but has no normalizeStatus yet (legacy
 *                pre-Phase-2 row). The clone gate lazy-backfills one on the
 *                next seed attempt.
 *   missing      No VoiceSnippet on the CharSpeechSample at all. This is
 *                an upstream data problem; there's nothing to normalize.
 */

export const NORMALIZE_STATUSES = [
  {
    key: 'missing',
    label: 'No Sample',
    color: 'grey',
    icon: 'question circle outline',
    terminal: true,
    virtual: true,
    retryable: false,
    description:
      'No VoiceSnippet is attached to this CharSpeechSample. The actor needs to upload a recording before cloning can run.'
  },
  {
    key: 'none',
    label: 'Not Normalized',
    color: 'grey',
    icon: 'circle outline',
    terminal: false,
    virtual: true,
    retryable: true,
    description:
      'Legacy sample uploaded before the normalizer existed. The clone gate will lazy-backfill; you can also click Retry to start it immediately.'
  },
  {
    key: 'pending',
    label: 'Normalize Queued',
    color: 'grey',
    icon: 'clock outline',
    terminal: false,
    retryable: false,
    description:
      'Queued for canonicalization to 44.1 kHz mono WAV. Usually flips to Ready within a few seconds.'
  },
  {
    key: 'processing',
    label: 'Normalizing',
    color: 'blue',
    icon: 'cogs',
    terminal: false,
    retryable: false,
    description:
      'Transcoding the uploaded audio to the canonical WAV format that ElevenLabs prefers.'
  },
  {
    key: 'ready',
    label: 'Normalized',
    color: 'green',
    icon: 'check circle',
    terminal: true,
    retryable: false,
    description:
      'Source audio is canonicalized and ready to feed the ElevenLabs clone pipeline.'
  },
  {
    key: 'failed',
    label: 'Normalize Failed',
    color: 'orange',
    icon: 'exclamation triangle',
    terminal: true,
    retryable: true,
    description:
      'The normalizer hit a transient or infrastructural problem (missing ffprobe, S3 timeout, …). Click Retry after the underlying issue is fixed.'
  },
  {
    key: 'rejected',
    label: 'Rejected',
    color: 'red',
    icon: 'ban',
    terminal: true,
    retryable: false,
    description:
      'The source audio was permanently rejected (no audio stream, exceeded the maximum duration, etc.). The actor needs to re-record — retry will not help.'
  }
];

export const NORMALIZE_STATUS_MAP = NORMALIZE_STATUSES.reduce((acc, s) => {
  acc[s.key] = s;
  return acc;
}, {});

export const getNormalizeStatus = (key) =>
  NORMALIZE_STATUS_MAP[key] || NORMALIZE_STATUS_MAP.none;

/**
 * Derive a normalize-status key from a pipeline row. Centralizes the
 * "no normalize block at all" vs "normalize.status == null" vs "has a value"
 * decision so the page component doesn't have to duplicate it in three places.
 */
export function deriveNormalizeStatusKey(normalize) {
  if (!normalize) return 'missing';            // no VoiceSnippet attached
  if (!normalize.status) return 'none';        // legacy row, never seeded
  return normalize.status;                     // pending|processing|ready|failed|rejected
}

export const IN_FLIGHT_NORMALIZE_STATUSES = NORMALIZE_STATUSES
  .filter((s) => !s.terminal && !s.virtual)
  .map((s) => s.key);

export default NORMALIZE_STATUSES;
